'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './Input';
import { Modal } from './Modal';
import { Button } from './Button';
import { useTranslation } from '@/lib/i18n/useTranslation';
import { Unit, UNIT_CATEGORIES } from '@/types/unit';

interface UnitComboboxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  units: Unit[];
  onAddUnit: (unit: Unit) => void;
  placeholder?: string;
  required?: boolean;
}

export const UnitCombobox: React.FC<UnitComboboxProps> = ({
  label,
  value,
  onChange,
  units,
  onAddUnit,
  placeholder,
  required = false,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // New unit form state
  const [newUnit, setNewUnit] = useState<Partial<Unit>>({
    name: '',
    symbol: '',
    parentId: null,
    parentName: null,
    conversionRatio: 1,
    category: 'Quantity',
  });

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredUnits = useMemo(() => {
    const lowerInput = inputValue.toLowerCase();
    return units
      .filter((unit) => 
        unit.name.toLowerCase().includes(lowerInput) ||
        unit.symbol.toLowerCase().includes(lowerInput)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [inputValue, units]);

  // Group units by category
  const groupedUnits = useMemo(() => {
    const groups: Record<string, Unit[]> = {};
    filteredUnits.forEach((unit) => {
      if (!groups[unit.category]) {
        groups[unit.category] = [];
      }
      groups[unit.category].push(unit);
    });
    return groups;
  }, [filteredUnits]);

  const showAddNew = inputValue && !units.some(u => u.name.toLowerCase() === inputValue.toLowerCase());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSelectUnit = (unit: Unit) => {
    setInputValue(unit.name);
    onChange(unit.name);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleOpenCreateModal = () => {
    setNewUnit({
      name: inputValue,
      symbol: '',
      parentId: null,
      parentName: null,
      conversionRatio: 1,
      category: 'Quantity',
    });
    setShowCreateModal(true);
    setIsOpen(false);
  };

  const handleCreateUnit = () => {
    if (newUnit.name && newUnit.symbol) {
      const parentUnit = units.find(u => u.id === newUnit.parentId);
      const createdUnit: Unit = {
        id: `unit_${Date.now()}`,
        name: newUnit.name || '',
        symbol: newUnit.symbol || '',
        parentId: newUnit.parentId || null,
        parentName: parentUnit?.name || null,
        conversionRatio: newUnit.conversionRatio || 1,
        category: newUnit.category || 'Quantity',
      };
      onAddUnit(createdUnit);
      setInputValue(createdUnit.name);
      onChange(createdUnit.name);
      setShowCreateModal(false);
      setNewUnit({
        name: '',
        symbol: '',
        parentId: null,
        parentName: null,
        conversionRatio: 1,
        category: 'Quantity',
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const flatUnits = filteredUnits;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, flatUnits.length + (showAddNew ? 0 : -1))
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < flatUnits.length) {
        handleSelectUnit(flatUnits[highlightedIndex]);
      } else if (showAddNew && highlightedIndex === flatUnits.length) {
        handleOpenCreateModal();
      } else {
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get parent units for the selected category (only base units or units with fewer hierarchy levels)
  const availableParentUnits = useMemo(() => {
    return units.filter(u => u.category === newUnit.category);
  }, [units, newUnit.category]);

  return (
    <>
      <div className="relative" ref={wrapperRef}>
        <div className="relative">
          <Input
            label={label}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t('inventory.selectOrTypeUnit')}
            ref={inputRef}
            required={required}
          />
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="absolute right-3 top-[38px] p-1 rounded-full text-foreground-muted hover:bg-background-secondary transition-colors"
            aria-label="Toggle options"
          >
            <svg
              className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-card-bg border border-card-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
            {Object.keys(groupedUnits).length > 0 || showAddNew ? (
              <>
                {Object.entries(groupedUnits).map(([category, categoryUnits]) => (
                  <div key={category}>
                    <div className="px-3 py-1.5 text-xs font-semibold text-foreground-muted bg-background-secondary sticky top-0">
                      {category}
                    </div>
                    <ul>
                      {categoryUnits.map((unit) => {
                        const flatIndex = filteredUnits.indexOf(unit);
                        return (
                          <li
                            key={unit.id}
                            onClick={() => handleSelectUnit(unit)}
                            onMouseEnter={() => setHighlightedIndex(flatIndex)}
                            className={`px-4 py-2 cursor-pointer text-foreground hover:bg-background-secondary ${
                              value === unit.name ? 'bg-primary/10 text-primary font-medium' : ''
                            } ${highlightedIndex === flatIndex ? 'bg-background-secondary' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{unit.name}</span>
                              <span className="text-xs text-foreground-muted">({unit.symbol})</span>
                            </div>
                            {unit.parentName && (
                              <div className="text-xs text-foreground-muted">
                                {unit.conversionRatio} {unit.name} = 1 {unit.parentName}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
                {showAddNew && (
                  <div
                    onClick={handleOpenCreateModal}
                    onMouseEnter={() => setHighlightedIndex(filteredUnits.length)}
                    className={`px-4 py-3 cursor-pointer text-success hover:bg-background-secondary flex items-center gap-2 border-t border-card-border ${
                      highlightedIndex === filteredUnits.length ? 'bg-background-secondary' : ''
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>{t('inventory.createNewUnit')}: &quot;{inputValue}&quot;</span>
                  </div>
                )}
              </>
            ) : (
              <p className="px-4 py-3 text-foreground-muted">
                {t('inventory.noMatchingUnits')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Create Unit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t('inventory.createNewUnit')}
        size="md"
      >
        <div className="space-y-4">
          {/* Unit Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('inventory.unitCategory')} *
            </label>
            <select
              value={newUnit.category || 'Quantity'}
              onChange={(e) => setNewUnit({ ...newUnit, category: e.target.value, parentId: null, parentName: null })}
              className="w-full px-3 py-2 rounded-lg bg-card-bg border border-card-border text-foreground"
            >
              {UNIT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Unit Name & Symbol */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={`${t('inventory.unitName')} *`}
              value={newUnit.name || ''}
              onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
              placeholder="e.g., Kilogram"
            />
            <Input
              label={`${t('inventory.unitSymbol')} *`}
              value={newUnit.symbol || ''}
              onChange={(e) => setNewUnit({ ...newUnit, symbol: e.target.value })}
              placeholder="e.g., kg"
            />
          </div>

          {/* Parent Unit */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('inventory.parentUnit')}
            </label>
            <select
              value={newUnit.parentId || ''}
              onChange={(e) => {
                const parentId = e.target.value || null;
                const parent = units.find(u => u.id === parentId);
                setNewUnit({ 
                  ...newUnit, 
                  parentId, 
                  parentName: parent?.name || null,
                  conversionRatio: parentId ? (newUnit.conversionRatio || 1) : 1
                });
              }}
              className="w-full px-3 py-2 rounded-lg bg-card-bg border border-card-border text-foreground"
            >
              <option value="">{t('inventory.noParentUnit')}</option>
              {availableParentUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name} ({unit.symbol})
                </option>
              ))}
            </select>
            <p className="text-xs text-foreground-muted mt-1">
              {t('inventory.parentUnitHelp')}
            </p>
          </div>

          {/* Conversion Ratio */}
          {newUnit.parentId && (
            <div className="p-4 bg-background-secondary rounded-lg">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('inventory.conversionRatio')}
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={newUnit.conversionRatio || 1}
                  onChange={(e) => setNewUnit({ ...newUnit, conversionRatio: parseFloat(e.target.value) || 1 })}
                  className="w-24"
                  min={0.0001}
                  step={0.01}
                />
                <span className="text-foreground font-medium">{newUnit.name || t('inventory.thisUnit')}</span>
                <span className="text-foreground-muted">=</span>
                <span className="text-foreground font-medium">1</span>
                <span className="text-foreground font-medium">
                  {units.find(u => u.id === newUnit.parentId)?.name || t('inventory.parentUnit')}
                </span>
              </div>
              <p className="text-xs text-foreground-muted mt-2">
                {newUnit.conversionRatio || 1} {newUnit.name || t('inventory.thisUnit')} = 1 {units.find(u => u.id === newUnit.parentId)?.name || t('inventory.parentUnit')}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
            >
              {t('inventory.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateUnit}
              className="flex-1"
              disabled={!newUnit.name || !newUnit.symbol}
            >
              {t('inventory.createUnit')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

